import type {SharedKey} from './types';

const localStorage = typeof window !== 'undefined' ? window.localStorage : null;

export const isStorageSupported = ((): boolean => {
	if (!localStorage) return false;

	try {
		localStorage.setItem('__shared_storage__', '1');
		localStorage.removeItem('__shared_storage__');
	} catch {
		return false;
	}

	return true;
})();

const get = <T>(key: SharedKey, defaultValue?: T): T | undefined => {
	if (!isStorageSupported || !localStorage) {
		return defaultValue;
	}

	const item = localStorage.getItem(storage.formatKey(key));
	if (!item) return defaultValue;

	try {
		return JSON.parse(item) as T;
	} catch {
		return defaultValue;
	}
};

const storage = {
	get,

	set: <T>(key: SharedKey, value: T): boolean => {
		if (!isStorageSupported || !localStorage) {
			return false;
		}

		if (value === undefined || value === null) {
			localStorage.removeItem(storage.formatKey(key));
			return false;
		}

		localStorage.setItem(storage.formatKey(key), JSON.stringify(value));
		return true;
	},

	remove: (key: SharedKey): void => {
		if (!isStorageSupported || !localStorage) {
			return;
		}

		localStorage.removeItem(storage.formatKey(key));
	},

	isset: (key: SharedKey): boolean => {
		if (!isStorageSupported || !localStorage) {
			return false;
		}

		const formattedKey = storage.formatKey(key);

		return Boolean(
			typeof localStorage[formattedKey as keyof Storage] !== 'undefined' &&
			localStorage.getItem(formattedKey) !== null,
		);
	},

	clear: (): void => {
		if (!isStorageSupported || !localStorage) {
			return;
		}

		localStorage.removeItem(storage.formatKey('theme'));
		localStorage.removeItem(storage.formatKey('language'));
	},

	formatKey: (key: SharedKey): string => `shared-${key}`,
};

export default storage;
